'use client';

import Link from 'next/link';
import { useSignupCTA } from '@/lib/hooks/useSignupCTA';
import { useAuth } from '@/lib/auth';
import ComingSoonModal from './ComingSoonModal';

/**
 * Get display name from user data
 * Prefers name if it doesn't look like an email, otherwise extracts from email
 */
function getDisplayName(name: string | undefined, email: string | undefined): string {
  // If name exists and doesn't look like an email, use it
  if (name && !name.includes('@')) {
    // Get first name only for friendlier greeting
    return name.split(' ')[0];
  }

  // Fall back to extracting name from email
  if (email) {
    const localPart = email.split('@')[0];
    // Capitalize first letter
    return localPart.charAt(0).toUpperCase() + localPart.slice(1).replace(/[._]/g, ' ').split(' ')[0];
  }

  return '';
}

export default function HomepageHeroCTA() {
  const { isAuthenticated, user } = useAuth();
  const {
    shouldShow,
    isLoading,
    showComingSoonModal,
    handleSignupClick,
    closeModal,
  } = useSignupCTA();

  // Show Dashboard CTA for authenticated users
  if (isAuthenticated) {
    const displayName = getDisplayName(user?.name, user?.email);

    return (
      <div className="max-w-2xl mx-auto mt-8">
        <div className="bg-accent-muted dark:bg-accent-muted border border-border rounded-xl p-6 md:p-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-light-text dark:text-foreground mb-3">
            Welcome back{displayName ? `, ${displayName}` : ''}!
          </h2>
          <p className="text-base md:text-lg text-light-text-secondary dark:text-foreground-secondary mb-6">
            Continue your learning journey with personalized insights and tools.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              href="/dashboard"
              className="bg-accent text-accent-foreground px-6 py-3 rounded-lg font-semibold hover:bg-accent-hover transition-colors inline-flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              Go to Dashboard
            </Link>
            <Link
              href="/paths"
              className="bg-transparent border-2 border-accent text-accent px-6 py-3 rounded-lg font-semibold hover:bg-accent-muted transition-colors inline-block text-center"
            >
              Learning Paths
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Don't show signup CTA if feature is disabled
  if (!shouldShow) {
    return null;
  }

  return (
    <>
      <div className="max-w-2xl mx-auto mt-8">
        <div className="bg-accent-muted dark:bg-accent-muted border border-border rounded-xl p-6 md:p-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-light-text dark:text-foreground mb-3">
            Stop Reinventing the Wheel. Start Shipping.
          </h2>
          <p className="text-base md:text-lg text-light-text-secondary dark:text-foreground-secondary mb-4">
            Get battle-tested frameworks, decision tools, and templates used by CTOs at growing companies.
          </p>
          <ul className="text-sm text-light-text-secondary dark:text-foreground-secondary mb-6 space-y-1">
            <li>24+ interactive decision tools</li>
            <li>50+ frameworks for hiring, scaling, and architecture</li>
            <li>Ready-to-use templates and checklists</li>
          </ul>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleSignupClick}
              disabled={isLoading}
              className="bg-accent text-accent-foreground px-6 py-3 rounded-lg font-semibold hover:bg-accent-hover transition-colors disabled:opacity-50"
            >
              Get Started Free
            </button>
            <Link
              href="/tools"
              className="bg-transparent border-2 border-accent text-accent px-6 py-3 rounded-lg font-semibold hover:bg-accent-muted transition-colors inline-block text-center"
            >
              Explore Tools
            </Link>
          </div>
          <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-4">
            No credit card required
          </p>
        </div>
      </div>

      <ComingSoonModal
        isOpen={showComingSoonModal}
        onClose={closeModal}
        type="signup"
      />
    </>
  );
}
